import React from "react";
import {Navigate, RouteObject, useLocation, useParams} from "react-router-dom";
import {Redirector} from "./Pages/Redirector";
import EmailConfirmedPage from "./Pages/EmailConfirmedPage";
import EmailConfirmationErrorPage from "./Pages/EmailConfirmationErrorPage";
import LoginPage from "./Pages/LoginPage";
import NotFoundPage from "./Pages/NotFoundPage";
import {PrivateRoute} from "./Guards/PrivateRouteGuard";
import {DefaultLayout} from "./Components/DefaultLayout";
import EventsPage from "./Pages/Admin/EventsPage";
import ParticipantsPage from "./Pages/Admin/ParticipantsPage";
import PublicEventFormsPage from "./Pages/Admin/PublicEventFormsPage";
import EventSetupPage from "./Pages/Admin/EventSetupPage";
import EventBasicsPage from "./Pages/Admin/eventConfig/EventBasicsPage";
import EventFeesPage from "./Pages/Admin/eventConfig/EventFeesPage";
import EventAccommodationPage from "./Pages/Admin/eventConfig/EventAccommodationPage";
import EventActivitiesPage from "./Pages/Admin/eventConfig/EventActivitiesPage";
import {OrganizerWorkspaceRoles} from "./Static/OrganizerRoles";
import PublicStripePaymentPage from "./Pages/PublicStripePaymentPage";
import PublicRegistrationPage from "./Pages/PublicRegistrationPage";
import PublicRegistrationEmailConfirmationPage from "./Pages/PublicRegistrationEmailConfirmationPage";
import PublicCertificateRequestPage from "./Pages/PublicCertificateRequestPage";
import PublicInvitationPage from "./Pages/PublicInvitationPage";
import NotificationsPage from "./Pages/Admin/NotificationsPage";
import GeneralSettingsPage from "./Pages/Admin/GeneralSettingsPage";
import EventWorkspaceLayout from "./Pages/Admin/EventWorkspaceLayout";
import EventRoomingListPage from "./Pages/Admin/EventRoomingListPage";
import EventPaymentsPage from "./Pages/Admin/EventPaymentsPage";
import {resolveLegacyAdminRoute, type LegacyWorkspaceSection} from "./Pages/Admin/adminCompatibilityRoutes";
import ParticipantDetailPage from "./Pages/Admin/participantDetail/ParticipantDetailPage";
import EventCheckInStationPage from "./Pages/Admin/EventCheckInStationPage";
import EventCertificateTemplatePage from "./Pages/Admin/EventCertificateTemplatePage";
import EventBadgeTemplatePage from "./Pages/Admin/EventBadgeTemplatePage";
import EventAuditPage from "./Pages/Admin/EventAuditPage";
import HotelsPage from "./Pages/Admin/HotelsPage";
import PayersPage from "./Pages/Admin/PayersPage";
import UserProfilePage from "./Pages/Admin/UserProfilePage";
import AboutAppPage from "./Pages/Admin/AboutAppPage";

const LegacyEventSetupRedirect = () => {
    const {eventId} = useParams();

    return <Navigate to={`/Admin/Events/${eventId}/overview`} replace/>;
};

type LegacyAdminRouteRedirectProps = {
    section?: LegacyWorkspaceSection;
    fallbackPath?: string;
};

const LegacyAdminRouteRedirect = ({section, fallbackPath}: LegacyAdminRouteRedirectProps) => {
    const location = useLocation();

    return <Navigate to={resolveLegacyAdminRoute(location.search, {section, fallbackPath})} replace/>;
};

const AdminRoutes: RouteObject[] = [
    {path: "", element: <Navigate to="Events" replace/>},
    {path: "Events", element: <EventsPage view="active"/>},
    {path: "Events/Archive", element: <EventsPage view="archived"/>},
    {path: "Profile", element: <UserProfilePage/>},
    {path: "About", element: <AboutAppPage/>},
    {path: "Hotels", element: <HotelsPage/>},
    {path: "Hotels/:hotelId", element: <HotelsPage/>},
    {path: "Payers", element: <PayersPage/>},
    {path: "Payers/:payerId", element: <PayersPage/>},
    {
        path: "Events/:eventId",
        element: <EventWorkspaceLayout/>,
        children: [
            {index: true, element: <Navigate to="overview" replace/>},
            {path: "overview", element: <EventSetupPage/>},
            {path: "settings", element: <EventBasicsPage/>},
            {path: "registration-fees", element: <EventFeesPage/>},
            {path: "accommodation", element: <EventAccommodationPage/>},
            {path: "activities", element: <EventActivitiesPage/>},
            {path: "registration-form", element: <PublicEventFormsPage/>},
            {path: "certificate-template", element: <EventCertificateTemplatePage/>},
            {path: "badge-template", element: <EventBadgeTemplatePage/>},
            {path: "participants", element: <ParticipantsPage/>},
            {path: "participants/:participantId", element: <ParticipantDetailPage/>},
            {path: "check-in", element: <EventCheckInStationPage/>},
            {path: "rooming", element: <EventRoomingListPage/>},
            {path: "payments", element: <EventPaymentsPage/>},
            {path: "notifications", element: <NotificationsPage/>},
            {path: "audit", element: <EventAuditPage/>},
            {path: "*", element: <Navigate to="overview" replace/>}
        ]
    },
    {path: "EventSetup/:eventId", element: <LegacyEventSetupRedirect/>},
    {path: "RegistrationForms", element: <LegacyAdminRouteRedirect section="registration-form"/>},
    {path: "Accommodations", element: <LegacyAdminRouteRedirect section="accommodation"/>},
    {path: "Participants", element: <LegacyAdminRouteRedirect section="participants"/>},
    {path: "Notifications", element: <LegacyAdminRouteRedirect section="notifications"/>},
    {path: "Payments", element: <LegacyAdminRouteRedirect section="payments"/>},
    {path: "GeneralSettings", element: <GeneralSettingsPage/>},
    {path: "OrganizationMembers", element: <LegacyAdminRouteRedirect fallbackPath="/Admin/GeneralSettings"/>},
];

const BaseRoutes: RouteObject[] = [
    {path: "/public/payments/:token", element: <PublicStripePaymentPage/>},
    {
        path: '/',
        element: <Redirector/>,
    },
    {
        path: "/EmailConfirmed",
        element: <EmailConfirmedPage/>,
    },
    {
        path: "/EmailConfirmationError",
        element: <EmailConfirmationErrorPage/>,
    },
    {
        path: '/Login',
        element: <LoginPage/>,
    },
    {
        path: '/SignUp',
        element: <Navigate to="/Login" replace/>,
    },
    {
        path: '/public/events/:eventIdentifier/register',
        element: <PublicRegistrationPage/>,
    },
    {
        path: '/public/events/:eventIdentifier/invitation',
        element: <PublicInvitationPage/>,
    },
    {
        path: '/public/events/:eventIdentifier/registrations/:registrationId/confirm-email',
        element: <PublicRegistrationEmailConfirmationPage/>,
    },
    {
        path: '/public/events/:eventIdentifier/certificate-request',
        element: <PublicCertificateRequestPage/>,
    },
    {
        path: '/Admin/*',
        element: <DefaultLayout>
            <PrivateRoute roles={OrganizerWorkspaceRoles}/>
        </DefaultLayout>,
        children: AdminRoutes,
    },
    {
        path: '*',
        element: <NotFoundPage/>,
    },
];

export default BaseRoutes;
